/**
 * Scale animation utility functions for AnimatedItem interface
 * Provides scaling entrance and exit animations with customizable behavior
 */

import { interpolate, Easing } from 'remotion';

export type ScaleDirection = 'up' | 'down';

/**
 * Calculate scale animation transform values
 * @param progress - Animation progress (0 to 1)
 * @param direction - Animation direction ('entrance' or 'exit')
 * @param scaleDirection - Direction of scale ('up' = grow in, 'down' = shrink in)
 * @param minScale - Minimum scale value (default: 0.3)
 * @param maxScale - Maximum scale value (default: 1.0)
 * @returns Transform values for current animation frame
 */
export function calculateScaleTransform(
  progress: number,
  direction: 'entrance' | 'exit',
  scaleDirection: ScaleDirection = 'up',
  minScale: number = 0.3,
  maxScale: number = 1.0
): { scale: number; opacity: number } {
  // Validate inputs
  if (progress < 0 || progress > 1) {
    console.warn(`Invalid progress value: ${progress}. Must be between 0 and 1.`);
    progress = Math.max(0, Math.min(1, progress));
  }
  
  if (minScale < 0 || maxScale < 0 || minScale > maxScale) {
    console.warn(`Invalid scale values: minScale=${minScale}, maxScale=${maxScale}. Must be positive and minScale <= maxScale.`);
    minScale = Math.max(0, Math.min(minScale, maxScale));
    maxScale = Math.max(minScale, maxScale);
  }

  // Use Remotion's interpolate with easing for smooth animation
  
  // Apply easing based on direction
  const easedProgress = direction === 'entrance' 
    ? interpolate(progress, [0, 1], [0, 1], {
        easing: Easing.out(Easing.cubic)
      })
    : interpolate(progress, [0, 1], [1, 0], {
        easing: Easing.in(Easing.cubic)
      });
  
  let scale: number;
  
  if (scaleDirection === 'up') {
    // Scale from min to max (grow in)
    scale = minScale + (maxScale - minScale) * easedProgress;
  } else {
    // Scale from max to min (shrink in) - FIXED: start at maxScale and shrink to minScale
    scale = maxScale - (maxScale - minScale) * easedProgress;
  }
  
  // Fade during scale for smoother appearance
  const opacity = easedProgress;
  
  return { scale, opacity };
}

/**
 * Get CSS transform for scale animation with hardware acceleration
 * @param scale - Current scale value
 * @param opacity - Current opacity value
 * @returns CSS style object for scale animation
 */
export function getScaleAnimationStyle(scale: number, opacity: number): React.CSSProperties {
  return {
    transform: `scale3d(${scale}, ${scale}, 1)`,
    opacity,
    willChange: 'transform, opacity',
    transformOrigin: 'center center',
  };
}

/**
 * Calculate scale animation for specific direction shortcuts
 */
export function calculateScaleUpAnimation(progress: number, direction: 'entrance' | 'exit'): React.CSSProperties {
  const { scale, opacity } = calculateScaleTransform(progress, direction, 'up');
  return getScaleAnimationStyle(scale, opacity);
}

export function calculateScaleDownAnimation(progress: number, direction: 'entrance' | 'exit'): React.CSSProperties {
  const { scale, opacity } = calculateScaleTransform(progress, direction, 'down');
  return getScaleAnimationStyle(scale, opacity);
}